/*
 * dv-compare.js — collection-page spec compare (tray + side-by-side modal).
 *
 * Each grid item carries data-dv-cmp-* identity attrs and data-dvf-* spec
 * attrs (stamped in main-collection-product-grid.liquid). Selection lives in
 * sessionStorage so it survives the native facet/sort AJAX swaps; a
 * MutationObserver re-syncs the per-card toggle buttons after each swap
 * (same pattern as dv-collection-filter.js). All dynamic text is set via
 * textContent — innerHTML is only used to clear containers. The modal is a
 * focus-managed dialog: focus moves to the close button on open, Tab is
 * trapped inside, and focus returns to the opener on close.
 */
(function () {
  'use strict';

  var KEY = 'dv-compare';
  var MAX = 3;
  var CONTAINER_SEL = '#ProductGridContainer';

  var ROWS = [
    { key: 'capacity', label: 'Capacity' },
    { key: 'interface', label: 'Interface' },
    { key: 'form', label: 'Form factor' },
    { key: 'memory', label: 'Memory' },
    { key: 'speed', label: 'Speed' },
    { key: 'heatsink', label: 'Cooling' },
    { key: 'condition', label: 'Condition' }
  ];

  function load() {
    try {
      var arr = JSON.parse(sessionStorage.getItem(KEY) || '[]');
      if (!Array.isArray(arr)) return [];
      // Drop anything that doesn't match the schema fromItem() writes — a
      // stale/foreign payload in sessionStorage must not throw in buildTable.
      return arr
        .filter(function (p) {
          return (
            p &&
            typeof p === 'object' &&
            typeof p.handle === 'string' &&
            p.handle !== '' &&
            typeof p.title === 'string' &&
            typeof p.url === 'string' &&
            typeof p.img === 'string' &&
            typeof p.price === 'string' &&
            p.specs &&
            typeof p.specs === 'object'
          );
        })
        .slice(0, MAX);
    } catch (e) {
      return [];
    }
  }

  function save(items) {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(items));
    } catch (e) {
      /* private mode / quota: selection just won't persist across swaps */
    }
  }

  function fromItem(li) {
    var p = {
      handle: li.getAttribute('data-dv-cmp-handle') || '',
      title: li.getAttribute('data-dv-cmp-title') || '',
      url: li.getAttribute('data-dv-cmp-url') || '',
      img: li.getAttribute('data-dv-cmp-img') || '',
      price: li.getAttribute('data-dv-cmp-price') || '',
      specs: {}
    };
    ROWS.forEach(function (r) {
      p.specs[r.key] = (li.getAttribute('data-dvf-' + r.key) || '').trim();
    });
    return p;
  }

  var tray, itemsEl, countEl, openBtn, modal, tableEl;
  var items = [];
  var lastFocus = null;

  function has(handle) {
    return items.some(function (p) { return p.handle === handle; });
  }

  function flashTray() {
    if (!tray) return;
    tray.classList.add('is-full');
    setTimeout(function () { tray.classList.remove('is-full'); }, 350);
  }

  function toggleFrom(li) {
    if (!li) return;
    var handle = li.getAttribute('data-dv-cmp-handle');
    if (!handle) return;
    if (has(handle)) {
      items = items.filter(function (p) { return p.handle !== handle; });
    } else {
      if (items.length >= MAX) return flashTray();
      items.push(fromItem(li));
    }
    save(items);
    renderTray();
    syncToggles();
  }

  function renderTray() {
    if (!tray) return;
    itemsEl.innerHTML = '';
    items.forEach(function (p) {
      var chip = document.createElement('span');
      chip.className = 'dv-cmp-chip';
      if (p.img) {
        var img = document.createElement('img');
        img.src = p.img;
        img.alt = '';
        img.width = 30;
        img.height = 30;
        chip.appendChild(img);
      }
      var t = document.createElement('span');
      t.className = 'dv-cmp-chip__t';
      t.textContent = p.title;
      chip.appendChild(t);
      var x = document.createElement('button');
      x.type = 'button';
      x.className = 'dv-cmp-chip__x';
      x.setAttribute('data-dv-cmp-remove', p.handle);
      x.setAttribute('aria-label', 'Remove ' + p.title + ' from compare');
      x.textContent = '×';
      chip.appendChild(x);
      itemsEl.appendChild(chip);
    });
    tray.hidden = items.length === 0;
    countEl.textContent = ' (' + items.length + ')';
    openBtn.disabled = items.length < 2;
    reserveTraySpace();
  }

  // The tray is position:fixed over the page bottom; pad <body> by its height
  // while it is open so the footer stays scrollable into view.
  function reserveTraySpace() {
    if (!tray) return;
    document.body.style.paddingBottom = tray.hidden ? '' : tray.offsetHeight + 'px';
  }

  function buildTable() {
    tableEl.innerHTML = '';
    var table = document.createElement('table');
    table.className = 'dv-cmp-table';

    var thead = document.createElement('thead');
    var hr = document.createElement('tr');
    hr.appendChild(document.createElement('th'));
    items.forEach(function (p) {
      var th = document.createElement('th');
      th.scope = 'col';
      if (p.img) {
        var img = document.createElement('img');
        img.src = p.img;
        img.alt = '';
        img.width = 72;
        img.height = 72;
        th.appendChild(img);
      }
      var a = document.createElement('a');
      a.href = p.url;
      a.textContent = p.title;
      th.appendChild(a);
      var price = document.createElement('div');
      price.className = 'dv-cmp-price dv-mono';
      price.textContent = p.price;
      th.appendChild(price);
      hr.appendChild(th);
    });
    thead.appendChild(hr);
    table.appendChild(thead);

    var tbody = document.createElement('tbody');
    ROWS.forEach(function (r) {
      var any = items.some(function (p) { return p.specs[r.key]; });
      if (!any) return;
      var tr = document.createElement('tr');
      var th = document.createElement('th');
      th.scope = 'row';
      th.textContent = r.label;
      tr.appendChild(th);
      items.forEach(function (p) {
        var td = document.createElement('td');
        td.className = 'dv-mono';
        td.textContent = p.specs[r.key] || '—';
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    tableEl.appendChild(table);
  }

  function openModal(trigger) {
    if (items.length < 2) return;
    buildTable();
    lastFocus = trigger || document.activeElement;
    modal.hidden = false;
    document.documentElement.classList.add('dv-cmp-lock');
    var closeBtn = modal.querySelector('[data-dv-cmp-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    modal.hidden = true;
    document.documentElement.classList.remove('dv-cmp-lock');
    if (lastFocus && document.documentElement.contains(lastFocus)) lastFocus.focus();
    lastFocus = null;
  }

  function syncToggles() {
    var els = document.querySelectorAll('.grid__item[data-dv-cmp-handle]');
    Array.prototype.forEach.call(els, function (li) {
      var btn = li.querySelector('[data-dv-cmp-toggle]');
      if (!btn) return;
      var on = has(li.getAttribute('data-dv-cmp-handle'));
      var want = on ? '✓ Compare' : '+ Compare';
      if (btn.textContent !== want) btn.textContent = want;
      var ap = on ? 'true' : 'false';
      if (btn.getAttribute('aria-pressed') !== ap) btn.setAttribute('aria-pressed', ap);
    });
  }

  function init() {
    tray = document.querySelector('[data-dv-cmp-tray]');
    modal = document.querySelector('[data-dv-cmp-modal]');
    if (!tray || !modal) return; // not a collection page
    itemsEl = tray.querySelector('[data-dv-cmp-items]');
    countEl = tray.querySelector('[data-dv-cmp-count]');
    openBtn = tray.querySelector('[data-dv-cmp-open]');
    tableEl = modal.querySelector('[data-dv-cmp-table]');
    items = load();

    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t.closest) return;
      var toggle = t.closest('[data-dv-cmp-toggle]');
      if (toggle) return toggleFrom(toggle.closest('.grid__item'));
      var rm = t.closest('[data-dv-cmp-remove]');
      if (rm) {
        var h = rm.getAttribute('data-dv-cmp-remove');
        items = items.filter(function (p) { return p.handle !== h; });
        save(items);
        renderTray();
        syncToggles();
        return;
      }
      var open = t.closest('[data-dv-cmp-open]');
      if (open) return openModal(open);
      if (t.closest('[data-dv-cmp-close]')) return closeModal();
      if (t.closest('[data-dv-cmp-clear]')) {
        items = [];
        save(items);
        renderTray();
        syncToggles();
        return;
      }
      if (t === modal) return closeModal(); // backdrop click
    });

    document.addEventListener('keydown', function (e) {
      if (modal.hidden) return;
      if (e.key === 'Escape') return closeModal();
      if (e.key !== 'Tab') return;
      var focusables = modal.querySelectorAll('a[href], button:not([disabled])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && (document.activeElement === first || !modal.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !modal.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    });

    renderTray();
    syncToggles();

    window.addEventListener('resize', function () {
      if (!tray.hidden) reserveTraySpace();
    });

    var container = document.querySelector(CONTAINER_SEL);
    if (container && window.MutationObserver) {
      var t;
      new MutationObserver(function () {
        clearTimeout(t);
        t = setTimeout(syncToggles, 60);
      }).observe(container, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
